/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import type { Entrance, Path } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { useLocationStore, useMapControl } from '@ahm/react-map/store';
import {
  Button,
  Card,
  Divider,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useMutationState } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';
import PaymentSelection from './payment/payment-selection';
import PromoSelection from './promotion/promo-selection';
import QuestionIcon from '@/assets/icons/question.svg';
import InfoTooltip from '@/components/info-tooltip';
import { ModalName } from '@/components/modals';
import ModalBillingDetails from '@/components/modals/modal-billing-detail';
import CustomComponent from '@/components/modals/modal-custom-drawer';
import TransportationDocumentsModal, {
  transportDocsUrl,
} from '@/components/modals/transportation-documents-modal';
import { useBookingRequestBodies } from '@/features/booking/hooks/use-booking-request';
import { useCreateOrder } from '@/features/booking/hooks/use-create-order';
import { useEstimateBookingOrder } from '@/features/booking/hooks/use-estimate-order';
import { useFillData } from '@/hooks/use-fill-data';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function NationwideConfirmOrder() {
  const pathname = usePathname();
  const [isOpenBillDetails, setIsOpenBillDetails] = useState<boolean>(false);
  const [isOpenTransportationDocs, setIsOpenTransportationDocs] =
    useState<boolean>(false);

  const [paramPromo] = useQueryState(
    'promo_code',
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const tBooking = useTranslations('booking');
  const isMobile = useIsMobile();
  const paths = useLocationStore.use.paths();

  const { handleFillData } = useFillData();

  useEffect(() => {
    void handleFillData();
  }, [handleFillData]);

  const {
    selectedService,
    estimatedData,
    setEstimatedData,
    paymentMethod: selectedPaymentMethod,
    packageDetail,
    isLoadingStore,
    replacedEntrances,
  } = useBookingStore((state) => state);

  const nearbyEntrances = useMapControl((state) => state.nearbyEntrances);

  const toggleModal = (modal: ModalName) => {
    switch (modal) {
      case ModalName.BillingDetails:
        setIsOpenBillDetails(!isOpenBillDetails);
        break;
      case ModalName.TransportationDocuments:
        setIsOpenTransportationDocs(!isOpenTransportationDocs);
        break;
      default:
        break;
    }
  };

  const estimatedError = useMemo(() => {
    return Boolean(estimatedData?.error || !estimatedData);
  }, [estimatedData]);

  const estimatedTotalPrice = useMemo(() => {
    return (paths.length > 1 && estimatedData?.data?.total_price) || 0;
  }, [estimatedData, paths]);

  const mutationStatus = useMutationState({
    filters: { status: 'pending' },
    select: (mutation) => mutation.state.status,
  });

  const loading = useMemo(() => {
    return mutationStatus?.length > 0;
  }, [mutationStatus]);

  const { estimateRequestBody, createRequestBody } = useBookingRequestBodies(
    pathname,
    paramPromo
  );

  useEstimateBookingOrder(estimateRequestBody, pathname);

  // const prevEstimateRequestBodyRef = useRef<string | undefined>('');

  // useEffect(() => {
  //   const currentDependencies = JSON.stringify(estimateRequestBody);

  //   if (
  //     prevEstimateRequestBodyRef.current !== currentDependencies &&
  //     estimateRequestBody
  //   ) {
  //     onEstimateOrder(estimateRequestBody);
  //     setEstimatedRequestData({
  //       ...estimateRequestBody,
  //       package_images: packageImages,
  //     });
  //   }

  //   prevEstimateRequestBodyRef.current = currentDependencies;
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [onEstimateOrder, estimateRequestBody]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const handleCreateOrder = useCreateOrder(
    createRequestBody,
    pathname,
    selectedPaymentMethod,
    paramPromo,
    isMobile
  );

  const handleClickCreateOrder = async () => {
    if (estimatedData?.data?.require_transportation_documents) {
      toggleModal(ModalName.TransportationDocuments);
      return;
    }
    await handleCreateOrder();
  };

  // if (estimatedData?.data.require_transportation_documents) {
  //   toggleModal(ModalName.TransportationDocuments);
  //   return;
  // }

  const handleConfirmTransportationDocs = async () => {
    if (!createRequestBody) return;

    await handleCreateOrder();
  };

  const priceOrigin = useMemo(() => {
    return estimatedData?.data?.subtotal_price &&
      estimatedData.data.subtotal_price !== estimatedData.data.total_price &&
      estimatedData.data.voucher_discount > 0
      ? formatCurrency(estimatedData?.data?.subtotal_price)
      : null;
  }, [estimatedData]);

  // tìm path có lat, lng trùng với replacedEntrances item có enabled = false
  const findPathIndexForMarkers = (
    markers: (Entrance & { enabled?: boolean })[],
    paths: Path[]
  ) => {
    if (!markers || !paths || markers.length === 0) return -1;

    return paths.findIndex((path) => {
      return markers.some((marker) => {
        return (
          path.lat === marker.coordinates[0] &&
          path.lng === marker.coordinates[1] &&
          marker.enabled === false
        );
      });
    });
  };

  const pathIndexRestricted = useMemo(() => {
    if (!nearbyEntrances || nearbyEntrances.length === 0) return -1;
    return findPathIndexForMarkers(nearbyEntrances, paths);
  }, [nearbyEntrances, paths]);

  const disableCreateOrder = useMemo(() => {
    if (
      !packageDetail ||
      packageDetail?.length < 1 ||
      paths.length < 2 ||
      !selectedService?._id ||
      pathIndexRestricted >= 0
    )
      return true;
    return false;
  }, [packageDetail, selectedService, paths, pathIndexRestricted]);

  useEffect(() => {
    if (paths.length < 2) {
      setEstimatedData(null);
    }
  }, [paths]);

  return (
    <>
      <Card
        left="var(--_main-padding-left, calc(var(--booking-layout-navbar-offset, 0px) + var(--_main-padding)))"
        right="var(--_main-padding-right, calc(var(--booking-layout-aside-offset, 0px) + var(--_main-padding)))"
        radius={0}
        pos="fixed"
        bottom={0}
        p={0}
        shadow="buttonDock"
        styles={{
          root: {
            zIndex: 30,
            borderTopLeftRadius: rem(12),
            borderTopRightRadius: rem(12),
          },
        }}
      >
        {isMobile ? (
          <Stack px="md" pt={12} pb="lg" gap="xs">
            <Group wrap="nowrap" gap={8}>
              <PromoSelection />
              <Divider h={32} w={1} bg="neutral.3" />
              <PaymentSelection />
            </Group>
            <Divider />
            <Group justify="space-between">
              <Group wrap="nowrap" gap={4}>
                <Text size="sm" fw={700} c="neutral.6">
                  {tBooking('billing.total_fee')}
                </Text>
                {estimatedData ? (
                  <InfoTooltip
                    label={tBooking('billing.billing_detail')}
                    onClick={() => toggleModal(ModalName.BillingDetails)}
                  />
                ) : null}
              </Group>
              <Group wrap="nowrap">
                {isLoadingStore || loading ? (
                  <Skeleton my={rem(6)} h={rem(20)} w={rem(112)} />
                ) : (
                  <>
                    {priceOrigin ? (
                      <Text fz={14} td="line-through" c="neutral.5" fw={500}>
                        {priceOrigin}
                      </Text>
                    ) : null}
                    <Title
                      order={3}
                      c={estimatedData ? 'primary.6' : 'neutral.4'}
                      fz={rem(20)}
                      lh={rem(32)}
                      className="flex flex-row flex-nowrap items-center gap-1"
                    >
                      <span className="truncate">
                        {formatCurrency(estimatedTotalPrice)}
                      </span>
                    </Title>
                  </>
                )}
              </Group>
            </Group>
            <Button
              mt={8}
              size="md"
              h={rem(48)}
              fullWidth
              loading={isLoadingStore || loading}
              disabled={!!estimatedError}
              onClick={handleClickCreateOrder}
            >
              {tBooking('create_order')}
            </Button>
          </Stack>
        ) : (
          <Stack p="md" gap="md">
            <SimpleGrid cols={2} spacing={rem(8)}>
              <PaymentSelection />
              <PromoSelection />
            </SimpleGrid>
            <Divider />
            <Group wrap="nowrap" gap={rem(8)} align="center">
              <Stack gap={0} ta="left" flex={1}>
                <Text size="sm" fw={500} c="neutral.7">
                  {tBooking('billing.total_fee')}
                </Text>
                <Stack mih={40} gap={0}>
                  {isLoadingStore || loading ? (
                    <Skeleton my="xs" h={rem(20)} w={115} />
                  ) : (
                    <>
                      <Title
                        order={3}
                        pt={rem(4)}
                        c={estimatedData ? 'primary.6' : 'neutral.4'}
                        fz={rem(20)}
                        lh={rem(32)}
                        className="flex flex-row flex-nowrap items-center gap-1"
                      >
                        <span className="truncate">
                          {formatCurrency(estimatedTotalPrice)}
                        </span>
                        {estimatedData ? (
                          <InfoTooltip
                            label={tBooking('billing.billing_detail')}
                            onClick={() =>
                              toggleModal(ModalName.BillingDetails)
                            }
                          />
                        ) : null}
                      </Title>

                      {priceOrigin ? (
                        <Text fz={14} td="line-through" c="neutral.5" fw={500}>
                          {priceOrigin}
                        </Text>
                      ) : null}
                    </>
                  )}
                </Stack>
              </Stack>
              <Button
                size="md"
                h={rem(48)}
                w={rem(200)}
                loading={isLoadingStore || loading}
                disabled={disableCreateOrder || !!estimatedError}
                onClick={handleClickCreateOrder}
              >
                {tBooking('create_order')}
              </Button>
            </Group>
          </Stack>
        )}
      </Card>

      {/* Modal */}
      <CustomComponent
        optionStyle={{
          headerStyle: {
            minHeight: rem(56),
            borderBottom: `1px solid var(--mantine-color-neutral-1)`,
          },
        }}
        title={tBooking('billing.bill_title')}
        isOpen={isOpenBillDetails}
        onClose={() => toggleModal(ModalName.BillingDetails)}
      >
        <ModalBillingDetails
          onClose={() => toggleModal(ModalName.BillingDetails)}
        />
      </CustomComponent>

      <CustomComponent
        optionStyle={{
          headerStyle: {
            minHeight: rem(56),
            borderBottom: isMobile
              ? `1px solid var(--mantine-color-neutral-1)`
              : 'none',
          },
        }}
        title={isMobile ? 'Xác nhận chứng từ đi đường' : ''}
        isOpen={isOpenTransportationDocs}
        onClose={() => toggleModal(ModalName.TransportationDocuments)}
        rigthIconTitle={<QuestionIcon />}
        onClickRigthIcon={() => {
          window.open(transportDocsUrl, '_blank');
        }}
      >
        <TransportationDocumentsModal
          onClose={() => toggleModal(ModalName.TransportationDocuments)}
          onConfirm={handleConfirmTransportationDocs}
        />
      </CustomComponent>
    </>
  );
}
